import React, { useRef, useEffect, useState } from 'react';
import { Button } from '../../shared/components';

interface DrawingCanvasProps {
  onSave: (imageData: string) => void;
  onCancel: () => void;
}

const DrawingCanvas: React.FC<DrawingCanvasProps> = ({ onSave, onCancel }) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const [isDrawing, setIsDrawing] = useState(false);
  const [selectedColor, setSelectedColor] = useState('#000000');
  const [brushSize, setBrushSize] = useState(5);
  const [tool, setTool] = useState<'brush' | 'eraser'>('brush');

  const colors = [
    '#000000', '#4A4A4A', '#7F7F7F', '#FFFFFF',
    '#FF0000', '#FF6B6B', '#FFA07A', '#FFB6C1',
    '#00FF00', '#32CD32', '#98FB98', '#90EE90',
    '#0000FF', '#1E90FF', '#00BFFF', '#87CEEB',
    '#FFFF00', '#FFD700', '#FFA500', '#FF8C00',
    '#800080', '#BA55D3', '#FF00FF', '#9370DB',
    '#8B4513', '#D2691E', '#F4A460', '#DAA520'
  ];

  useEffect(() => {
    const canvas = canvasRef.current;
    const container = containerRef.current;
    if (!canvas || !container) return;

    const resizeCanvas = () => {
      const rect = container.getBoundingClientRect();
      const dpr = window.devicePixelRatio || 1;
      const width = Math.floor(rect.width);
      const height = Math.floor(rect.height);

      // Save current drawing before resize
      const tempCanvas = document.createElement('canvas');
      const tempCtx = tempCanvas.getContext('2d');
      const ctx = canvas.getContext('2d');
      if (!ctx || !tempCtx) return;

      tempCanvas.width = canvas.width;
      tempCanvas.height = canvas.height;
      tempCtx.drawImage(canvas, 0, 0);

      // Set new canvas size accounting for device pixel ratio
      canvas.width = width * dpr;
      canvas.height = height * dpr;
      canvas.style.width = `${width}px`;
      canvas.style.height = `${height}px`;

      // Scale context and restore drawing
      ctx.scale(dpr, dpr);
      ctx.fillStyle = '#FFFFFF';
      ctx.fillRect(0, 0, width, height);
      ctx.drawImage(tempCanvas, 0, 0, width, height);

      ctx.lineCap = 'round';
      ctx.lineJoin = 'round';
    };

    resizeCanvas();
    window.addEventListener('resize', resizeCanvas);
    return () => window.removeEventListener('resize', resizeCanvas);
  }, []);

  const getCoordinates = (e: React.MouseEvent | React.TouchEvent) => {
    const canvas = canvasRef.current;
    if (!canvas) return { x: 0, y: 0 };

    const rect = canvas.getBoundingClientRect();
    const dpr = window.devicePixelRatio || 1;
    let clientX, clientY;

    if ('touches' in e) {
      clientX = e.touches[0].clientX;
      clientY = e.touches[0].clientY;
    } else {
      clientX = (e as React.MouseEvent).clientX;
      clientY = (e as React.MouseEvent).clientY;
    }

    return {
      x: (clientX - rect.left),
      y: (clientY - rect.top)
    };
  };

  const startDrawing = (e: React.MouseEvent<HTMLCanvasElement> | React.TouchEvent<HTMLCanvasElement>) => {
    e.preventDefault();
    setIsDrawing(true);
    const canvas = canvasRef.current;
    if (!canvas) return;

    const context = canvas.getContext('2d');
    if (!context) return;

    const { x, y } = getCoordinates(e);
    context.beginPath();
    context.moveTo(x, y);
  };

  const draw = (e: React.MouseEvent<HTMLCanvasElement> | React.TouchEvent<HTMLCanvasElement>) => {
    e.preventDefault();
    if (!isDrawing) return;

    const canvas = canvasRef.current;
    if (!canvas) return;

    const context = canvas.getContext('2d');
    if (!context) return;

    const { x, y } = getCoordinates(e);

    if (tool === 'eraser') {
      context.globalCompositeOperation = 'destination-out';
      context.strokeStyle = 'rgba(0,0,0,1)';
    } else {
      context.globalCompositeOperation = 'source-over';
      context.strokeStyle = selectedColor;
    }

    context.lineWidth = brushSize;
    context.lineTo(x, y);
    context.stroke();
    context.beginPath();
    context.moveTo(x, y);
  };

  const stopDrawing = () => {
    setIsDrawing(false);
    const canvas = canvasRef.current;
    if (!canvas) return;
    const context = canvas.getContext('2d');
    if (!context) return;
    context.beginPath();
  };

  const clearCanvas = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const context = canvas.getContext('2d');
    if (!context) return;
    context.globalCompositeOperation = 'source-over';
    const dpr = window.devicePixelRatio || 1;
    context.fillStyle = '#FFFFFF';
    context.fillRect(0, 0, canvas.width / dpr, canvas.height / dpr);
  };

  const handleSave = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const imageData = canvas.toDataURL('image/png');
    onSave(imageData);
  };

  return (
    <div className="fixed inset-0 bg-slate-900/90 z-50 flex flex-col">
      {/* Header */}
      <div className="flex items-center justify-between p-3 bg-white border-b border-slate-200">
        <div>
          <h2 className="text-lg font-bold text-gray-800">Dibuja lo que sientes 🎨</h2>
          <p className="text-xs text-gray-600">Expresa tus emociones dibujando</p>
        </div>
        <div className="flex gap-2">
          <Button onClick={handleSave} className="text-sm py-1 px-3">Guardar</Button>
          <Button onClick={onCancel} variant="secondary" className="text-sm py-1 px-3">Salir</Button>
        </div>
      </div>

      {/* Toolbar */}
      <div className="bg-white border-b border-slate-200 p-2 flex flex-wrap items-center gap-3">
        {/* Tools */}
        <div className="flex gap-1">
          <button
            onClick={() => setTool('brush')}
            className={`p-2 rounded-lg text-sm font-medium transition-all ${
              tool === 'brush' ? 'bg-blue-500 text-white' : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
            }`}
          >
            🖌️ Pincel
          </button>
          <button
            onClick={() => setTool('eraser')}
            className={`p-2 rounded-lg text-sm font-medium transition-all ${
              tool === 'eraser' ? 'bg-pink-500 text-white' : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
            }`}
          >
            🧽 Borrador
          </button>
          <button
            onClick={clearCanvas}
            className="p-2 rounded-lg text-sm font-medium bg-slate-100 text-slate-700 hover:bg-red-100 transition-all"
          >
            🗑️ Limpiar
          </button>
        </div>

        {/* Brush size */}
        <div className="flex items-center gap-2">
          <span className="text-xs text-gray-600">Tamaño:</span>
          <input
            type="range"
            min="1"
            max="50"
            value={brushSize}
            onChange={(e) => setBrushSize(Number(e.target.value))}
            className="w-24"
          />
          <span className="text-xs text-gray-600 w-6">{brushSize}</span>
        </div>

        {/* Current tool preview */}
        <div className="ml-auto flex items-center gap-2">
          <div
            className="rounded-full border-2 border-slate-300"
            style={{
              width: Math.max(8, brushSize),
              height: Math.max(8, brushSize),
              backgroundColor: tool === 'eraser' ? '#FFB6C1' : selectedColor
            }}
          />
        </div>
      </div>

      {/* Color palette */}
      <div className="bg-white border-b border-slate-200 p-2">
        <p className="text-xs text-gray-600 mb-1">Colores:</p>
        <div className="flex gap-1 flex-wrap">
          {colors.map((color) => (
            <button
              key={color}
              onClick={() => { setSelectedColor(color); setTool('brush'); }}
              className={`w-6 h-6 sm:w-7 sm:h-7 rounded-full border-2 transition-all ${
                selectedColor === color && tool === 'brush' ? 'border-blue-500 scale-110' : 'border-slate-200'
              }`}
              style={{ backgroundColor: color }}
            />
          ))}
        </div>
      </div>

      {/* Canvas container */}
      <div ref={containerRef} className="flex-1 relative bg-slate-100 p-2">
        <canvas
          ref={canvasRef}
          onMouseDown={startDrawing}
          onMouseMove={draw}
          onMouseUp={stopDrawing}
          onMouseLeave={stopDrawing}
          onTouchStart={startDrawing}
          onTouchMove={draw}
          onTouchEnd={stopDrawing}
          className="border-2 border-slate-300 rounded-lg cursor-crosshair shadow-lg bg-white"
          style={{ touchAction: 'none', width: '100%', height: '100%' }}
        />
      </div>
    </div>
  );
};

export default DrawingCanvas;
